import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits.
   *
   * A line whose quantity is permanently locked (snippets/cart-products.liquid renders both
   * buttons `disabled` for a Buy X Get Y gift line — assets/buy-x-get-y-cart-sync.js keeps its
   * quantity in sync with its trigger line, so it must never be user-adjustable) needs that to
   * stick: without checking `serverDisabledMinus`/`serverDisabledPlus` (set once, from the
   * server-rendered markup, by the base class's connectedCallback), this override recomputed both
   * buttons from min/max/value alone on every call and silently re-enabled the plus button the
   * moment it had no `quantity_rule.max` — which is the common case.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are otherwise always dynamically managed, per source control.
    if (!this.serverDisabledMinus) minusButton.disabled = value <= min;
    if (!this.serverDisabledPlus) plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
